import type { TalkThread } from "~/api/ideTypes";
import type { IdeSession } from "~/api/opsTypes";
import type { Dispatch } from "~/api/types";

/**
 * The pure half of `/talk`'s sessions board: how a list of IDE session rows becomes WINDOWS a
 * person recognises, grouped by the machine they run on, with every field of the row placed in
 * one area and every conversation attached to the window it is held with.
 *
 * Three things here are easy to get subtly wrong, which is why they are functions with tests
 * rather than expressions inside a component:
 *
 *  - **A window is a coordinate, not a session key.** A key is minted per activation, so one
 *    reload leaves two rows for one window (the dead twin stays listed for a day). Showing both
 *    doubles the board; matching a conversation by key loses it at the next reload.
 *  - **Every capability lands somewhere.** A capability this page has never heard of goes to
 *    "Other" — a new extension build must not have its abilities silently dropped from a page
 *    whose promise is "every single detail".
 *  - **Opening a window needs a window.** The web has no process to start VS Code with; an online
 *    window on the same machine does it, and only one that says it can.
 */

/** Declared by an extension that can open a folder in a new window when the site asks (≥ 0.6.29). */
export const REMOTE_OPEN_CAPABILITY = "ide.open.remote";

/** The payload surface of a "open this folder in a new window" dispatch. */
export const IDE_OPEN_SURFACE = "ide-open";

function normalizePath(path: string): string {
  let normalized = path.trim().replace(/\\/g, "/");
  while (normalized.length > 1 && normalized.endsWith("/")) {
    normalized = normalized.slice(0, -1);
  }
  return normalized.toLowerCase();
}

/**
 * `machine|folder`, the identity that survives a reload. Null when there is no folder: a window
 * with nothing open has no place a conversation could be resumed in, so it is only ever itself.
 */
export function coordinateOf(place: {
  machineId?: string | null;
  workspacePath?: string | null;
}): string | null {
  if (!place.workspacePath) {
    return null;
  }
  return `${(place.machineId ?? "").toLowerCase()}|${normalizePath(place.workspacePath)}`;
}

/** One window: its freshest activation, and the dead twins a reload left behind. */
export interface TalkWindow {
  /** The coordinate, or the session key for a window with no folder. Stable across reloads. */
  key: string;
  session: IdeSession;
  /** Older activations at the same coordinate, newest first. */
  earlier: IdeSession[];
}

function fresher(a: IdeSession, b: IdeSession): number {
  if (a.isOnline !== b.isOnline) {
    return a.isOnline ? -1 : 1;
  }
  return Date.parse(b.lastSeenAt) - Date.parse(a.lastSeenAt);
}

/**
 * Is this window CONNECTED to the workspace — signed in to it (`teamId`, where it registers and
 * where its dispatches and conversations live), or its folder bound to it (`boundTeamId`, where
 * its documents and flows sync)? The IDE lists on desk show only these (owner, 2026-10-02: "do not
 * show the ides that are not connected to the workspace"). No workspace yet ⇒ nothing is connected.
 */
export function isConnectedTo(
  session: Pick<IdeSession, "teamId" | "boundTeamId">,
  workspaceId: string | null | undefined,
): boolean {
  if (!workspaceId) {
    return false;
  }
  return session.teamId === workspaceId || session.boundTeamId === workspaceId;
}

/** Rows → windows: twins collapsed onto the live (else most recently seen) activation. */
export function windowsFrom(sessions: readonly IdeSession[]): TalkWindow[] {
  const byKey = new Map<string, IdeSession[]>();
  for (const session of sessions) {
    const key = coordinateOf(session) ?? `session:${session.sessionKey}`;
    byKey.set(key, [...(byKey.get(key) ?? []), session]);
  }
  return [...byKey.entries()]
    .map(([key, rows]) => {
      const [session, ...earlier] = [...rows].sort(fresher) as [IdeSession, ...IdeSession[]];
      return { key, session, earlier };
    })
    .sort((a, b) => fresher(a.session, b.session));
}

export interface MachineGroup {
  key: string;
  machineId: string | null;
  machineName: string | null;
  windows: TalkWindow[];
  online: number;
}

/** Windows by the machine they run on — the first thing a person asks is "which computer?". */
export function groupByMachine(windows: readonly TalkWindow[]): MachineGroup[] {
  const groups = new Map<string, MachineGroup>();
  for (const window of windows) {
    const { machineId, machineName } = window.session;
    const key = machineId ?? `name:${machineName ?? ""}`;
    const group = groups.get(key) ?? { key, machineId, machineName, windows: [], online: 0 };
    group.windows.push(window);
    group.online += window.session.isOnline ? 1 : 0;
    group.machineName ??= machineName;
    groups.set(key, group);
  }
  return [...groups.values()].sort((a, b) => b.online - a.online);
}

/** Every session key this window has had in the listed period (the live one first). */
export function keysOf(window: TalkWindow): string[] {
  return [window.session.sessionKey, ...window.earlier.map((s) => s.sessionKey)];
}

// ── capabilities ─────────────────────────────────────────────────────────────

export type CapabilityFamily = "flows" | "tasks" | "todos" | "ide" | "platform" | "other";

export interface CapabilityGroup {
  family: CapabilityFamily;
  items: Array<{ capability: string; label: string }>;
}

const FAMILY_BY_PREFIX: Record<string, CapabilityFamily> = {
  flow: "flows",
  task: "tasks",
  todo: "todos",
  ide: "ide",
  http: "platform",
  token_diag: "platform",
  orchestration: "platform",
};

const FAMILY_ORDER: CapabilityFamily[] = ["flows", "tasks", "todos", "ide", "platform", "other"];

/** `flow.start_stepped` → "start stepped"; a bare `http` keeps its own name. */
function capabilityLabel(capability: string): string {
  const dot = capability.indexOf(".");
  const rest = dot === -1 ? capability : capability.slice(dot + 1);
  return rest.replace(/[._]/g, " ");
}

/**
 * The declared capability list, by family, in a fixed order. Nothing is dropped: an unknown
 * prefix is "other", and a non-string entry (a malformed heartbeat) is shown as written.
 */
export function groupCapabilities(
  capabilities: readonly unknown[] | null | undefined,
): CapabilityGroup[] {
  const groups = new Map<CapabilityFamily, CapabilityGroup>();
  for (const entry of capabilities ?? []) {
    const capability = typeof entry === "string" ? entry : JSON.stringify(entry);
    const prefix = capability.split(".")[0] ?? capability;
    const family = FAMILY_BY_PREFIX[prefix] ?? "other";
    const group = groups.get(family) ?? { family, items: [] };
    group.items.push({ capability, label: capabilityLabel(capability) });
    groups.set(family, group);
  }
  return FAMILY_ORDER.flatMap((family) => {
    const group = groups.get(family);
    return group ? [group] : [];
  });
}

// ── what a window is doing, and what was said to it ──────────────────────────

const LIVE_DETAIL = new Set(["queued", "running", "awaiting-approval"]);

export function isLiveDispatch(dispatch: Pick<Dispatch, "status" | "statusDetail">): boolean {
  return dispatch.statusDetail
    ? LIVE_DETAIL.has(dispatch.statusDetail)
    : ["queued", "claimed", "running"].includes(dispatch.status);
}

/** The live dispatches aimed at, or claimed by, this window — oldest first (the running one). */
export function liveWorkOf(window: TalkWindow, dispatches: readonly Dispatch[]): Dispatch[] {
  const keys = new Set(keysOf(window));
  return dispatches
    .filter((d) => isLiveDispatch(d) && (keys.has(d.claimedBy ?? "") || keys.has(d.target ?? "")))
    .sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt));
}

/**
 * The conversations held with this window. Matched by coordinate first — that is what survives a
 * reload — and by session key for a window with no folder.
 */
export function threadsOf(window: TalkWindow, threads: readonly TalkThread[]): TalkThread[] {
  const keys = new Set(keysOf(window));
  const coordinate = coordinateOf(window.session);
  return threads.filter((thread) => {
    const place = coordinateOf(thread);
    if (coordinate && place) {
      return place === coordinate;
    }
    return thread.sessionKey !== null && keys.has(thread.sessionKey);
  });
}

/** The window a conversation is held with, if it is one of the listed ones. */
export function windowOfThread(
  thread: TalkThread | undefined,
  windows: readonly TalkWindow[],
): TalkWindow | null {
  if (!thread) {
    return null;
  }
  return windows.find((window) => threadsOf(window, [thread]).length > 0) ?? null;
}

// ── opening a new window ─────────────────────────────────────────────────────

/** Can this window open a folder in a NEW window on its machine when the site asks? */
export function canOpenWindows(session: IdeSession): boolean {
  return (
    session.isOwn &&
    session.isOnline &&
    !session.revokedAt &&
    session.capabilities.includes(REMOTE_OPEN_CAPABILITY)
  );
}

/** The online window on a machine that will do the opening, or null when none can. */
export function openerOn(machine: MachineGroup): IdeSession | null {
  return machine.windows.map((w) => w.session).find(canOpenWindows) ?? null;
}

/**
 * An absolute folder path, POSIX or Windows. The machine validates that it exists — this only
 * stops a relative path, which would resolve against whatever folder the opener has open.
 */
export function isAbsoluteFolder(path: string): boolean {
  const trimmed = path.trim();
  return trimmed.startsWith("/") || /^[A-Za-z]:[\\/]/.test(trimmed);
}

/** The window that appeared for an open request: same machine, that folder, started after it. */
export function openedWindow(
  windows: readonly TalkWindow[],
  request: { machineId: string | null; path: string; askedAt: number },
): TalkWindow | null {
  const wanted = coordinateOf({ machineId: request.machineId, workspacePath: request.path });
  return (
    windows.find(
      (window) =>
        window.key === wanted &&
        window.session.isOnline &&
        // A minute of slack: the two clocks are the server's and the browser's.
        Date.parse(window.session.startedAt) >= request.askedAt - 60_000,
    ) ?? null
  );
}

// ── time ─────────────────────────────────────────────────────────────────────

/** "3 d 4 h", "2 h 10 min", "5 min" — how long a window has been connected. */
export function durationText(fromIso: string, now: number = Date.now()): string {
  const minutes = Math.max(0, Math.floor((now - Date.parse(fromIso)) / 60_000));
  if (!Number.isFinite(minutes)) {
    return "";
  }
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = minutes % 60;
  if (days > 0) {
    return `${days} d ${hours} h`;
  }
  if (hours > 0) {
    return `${hours} h ${mins} min`;
  }
  return `${mins} min`;
}
